import { useEffect, useRef } from 'react';

import { W3SSdk } from '@circle-fin/w3s-pw-web-sdk';

enum SDKErrorCode {
  unknown = -1,
  success = 0,
  apiParameterMissing = 1,
  apiParameterInvalid = 2,
  forbidden = 3,
  unauthorized = 4,
  retry = 9,
  customerSuspended = 10,
  pending = 11,
  invalidSession = 12,
  invalidPartnerId = 13,
  invalidMessage = 14,
  invalidPhone = 15,
  userAlreadyExisted = 155101,
  userNotFound = 155102,
  userTokenNotFound = 155103,
  userTokenExpired = 155104,
  invalidUserToken = 155105,
  userWasInitialized = 155106,
  userHasSetPin = 155107,
  userHasSetSecurityQuestion = 155108,
  userWasDisabled = 155109,
  userDoesNotSetPinYet = 155110,
  userDoesNotSetSecurityQuestionYet = 155111,
  incorrectUserPin = 155112,
  incorrectDeviceId = 155113,
  incorrectAppId = 155114,
  incorrectSecurityAnswers = 155115,
  invalidChallengeId = 155116,
  invalidApproveContent = 155117,
  invalidEncryptionKey = 155118,
  userPinLocked = 155119,
  securityAnswersLocked = 155120,
  userOTPTokenExpiredError = 155130,
  userOTPTokenInvalidError = 155131,
  userOTPNotFoundError = 155132,
  userOTPInvalidError = 155133,
  userOTPNotMatchError = 155134,
  userEmailInvalidError = 155135,
  userEmailMismatchError = 155136,
  deviceIDInvalidError = 155137,
  emailSendingFailedError = 155138,
  socialLoginTokenExpiredError = 155139,
  socialLoginProviderAppIDNotMatchError = 155140,
  userOTPIsLockedError = 155141,
  userOTPSendCountsOverLimitError = 155142,
  deviceTokenExpiredError = 155143,
  deviceTokenInvalidError = 155144,
  deviceTokenNotFoundError = 155145,
  notEnoughFunds = 155201,
  notEnoughBalance = 155202,
  exceedWithdrawLimit = 155203,
  minimumFundsRequired = 155204,
  invalidTransactionFee = 155205,
  rejectedOnAmlScreening = 155206,
  tagRequired = 155207,
  gasLimitTooLow = 155208,
  transactionDataNotEncodedProperly = 155209,
  fullNodeReturnedError = 155210,
  walletSetupRequired = 155211,
  lowerThenMinimumAccountBalance = 155212,
  rejectedByBlockchain = 155213,
  droppedAsPartOfReorg = 155214,
  operationNotSupport = 155215,
  amountBelowMinimum = 155216,
  wrongNftTokenIdNumber = 155217,
  invalidDestinationAddress = 155218,
  tokenWalletChainMismatch = 155219,
  wrongAmountsNumber = 155220,
  walletIsFrozen = 155501,
  maxWalletLimitReached = 155502,
  walletSetIdMutuallyExclusive = 155503,
  metadataUnmatched = 155504,
  userCanceled = 155701,
  launchUiFailed = 155702,
  pinCodeNotMatched = 155703,
  insecurePinCode = 155704,
  hintsMatchAnswers = 155705,
  networkError = 155706,
  userSecretMissing = 155717,
  invalidUserTokenFormat = 155718,
  userTokenMismatch = 155719,
  walletIdNotFound = 156001,
  tokenIdNotFound = 156002,
  transactionIdNotFound = 156003,
  entityCredentialNotFound = 156004,
  walletSetIdNotFound = 156005,
}

type ExecuteChallengePayload = {
  challengeId: string;
  userToken: string;
  encryptionKey: string;
};

interface ChallengeError extends Error {
  isUserCancelled?: boolean;
}

type ExecuteChallengeResult = { data: { signature?: string }; error: null } | { data: null; error: ChallengeError };

interface UseWalletSDK {
  executeChallenge: (payload: ExecuteChallengePayload) => Promise<ExecuteChallengeResult>;
}

export const useWalletSDK = (): UseWalletSDK => {
  const sdkRef = useRef<W3SSdk | null>(null);

  const executeChallenge = async (payload: ExecuteChallengePayload): Promise<ExecuteChallengeResult> => {
    const sdk = sdkRef.current;

    if (!sdk) {
      return {
        data: null,
        error: new Error('Circle Web SDK is not initialized'),
      };
    }

    return new Promise((resolve) => {
      try {
        sdk.setAuthentication({
          userToken: payload.userToken,
          encryptionKey: payload.encryptionKey,
        });

        sdk.execute(payload.challengeId, (error, result) => {
          if (error) {
            console.error('Error executing challenge:', error);
            resolve({
              data: null,
              error: {
                ...error,
                name: 'Execute challenge failed',
                isUserCancelled: error.code && (error.code as number) === SDKErrorCode.userCanceled,
              } as ChallengeError,
            });
            return;
          }

          const signature = result && 'data' in result ? result.data?.signature : undefined;

          resolve({
            data: { signature },
            error: null,
          });
        });
      } catch (error: any) {
        console.error('Error executing challenge:', error);
        resolve({
          data: null,
          error: {
            ...error,
            name: 'Execute challenge failed',
            isUserCancelled: error.code && (error.code as number) === SDKErrorCode.userCanceled,
          } as ChallengeError,
        });
      }
    });
  };
  useEffect(() => {
    const initSdk = async () => {
      try {
        const sdk = new W3SSdk({
          appSettings: { appId: process.env.REACT_APP_CIRCLE_APP_ID || '' },
        });

        sdkRef.current = sdk;
      } catch (err) {
        console.error('Failed to initialize Circle Web SDK:', err);
      }
    };

    void initSdk();

    let currentIframe: HTMLIFrameElement | null = null;

    const hideModal = () => {
      const appModal = document.getElementById('app-modal');
      appModal?.classList.add('hidden');
    };

    const showModal = () => {
      const appModal = document.getElementById('app-modal');
      appModal?.classList.remove('hidden');
    };

    const attachToIframe = (iframe: HTMLIFrameElement) => {
      if (currentIframe === iframe) return;

      currentIframe = iframe;

      iframe.addEventListener(
        'load',
        () => {
          hideModal();
        },
        { once: true },
      );
    };

    const observer = new MutationObserver(() => {
      const sdkIframe = document.getElementById('sdkIframe') as HTMLIFrameElement | null;

      if (sdkIframe) {
        attachToIframe(sdkIframe);
      } else if (currentIframe) {
        currentIframe = null;
        showModal();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const existingIframe = document.getElementById('sdkIframe') as HTMLIFrameElement | null;

    if (existingIframe) {
      attachToIframe(existingIframe);
    }

    return () => {
      sdkRef.current = null;
      observer.disconnect();
    };
  }, []);

  return {
    executeChallenge,
  };
};
